import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { motion } from "framer-motion";
import { useT } from "@/i18n/helpers";
import { useLanguage } from "@/i18n/LanguageContext";
import { useActualites } from "@/data/hooks";
import { FadeUp, Stagger, StaggerItem } from "@/components/Reveal";
import Seo from "@/components/Seo";

/* ------------------------------------------------------------------ */
/*  Home 2026, minimaliste premium : Hero + 4 Univers + Manifeste    */
/* ------------------------------------------------------------------ */

const Hero = () => {
  const tt = useT();
  return (
    <section data-testid="home-hero" className="bg-white">
      <div className="mx-auto max-w-7xl px-6 sm:px-12 pt-36 md:pt-48 pb-14 md:pb-20">
        <FadeUp>
          <div className="label-overline text-nanda-muted mb-8">
            {tt("Depuis 2023", "Since 2023", "Desde 2023")}
          </div>
        </FadeUp>
        <FadeUp delay={0.1}>
          <h1 className="font-sans font-light tracking-tight text-nanda-ink text-5xl sm:text-6xl md:text-7xl lg:text-[96px] leading-[1.02] max-w-5xl text-balance">
            {tt("Une marque.", "One brand.", "Uma marca.")}
            <br />
            <span className="text-nanda-inkSoft">{tt("Plusieurs savoir-faire.", "Several crafts.", "Vários saberes.")}</span>
          </h1>
        </FadeUp>
        <FadeUp delay={0.2}>
          <p className="mt-10 max-w-2xl text-nanda-inkSoft text-lg md:text-xl font-light leading-relaxed">
            {tt(
              "NANDA SÁ installe le solaire, fabrique des cosmétiques naturels, vend des produits du terroir et forme, en Guinée-Bissau et au Sénégal. Quatre activités, une seule marque.",
              "NANDA SÁ installs solar, makes natural cosmetics, sells terroir products and trains, in Guinea-Bissau and Senegal. Four activities, one single brand.",
              "A NANDA SÁ instala o solar, fabrica cosméticos naturais, vende produtos do terroir e forma, na Guiné-Bissau e no Senegal. Quatro actividades, uma só marca."
            )}
          </p>
        </FadeUp>
        <FadeUp delay={0.3} className="mt-12 flex flex-wrap items-center gap-4">
          <a
            href="#univers"
            data-testid="hero-cta-discover"
            className="inline-flex items-center gap-2.5 bg-nanda-terracotta text-white rounded-full px-7 py-3.5 text-sm font-medium hover:bg-[#8F5B3F] transition-colors duration-300"
          >
            {tt("Découvrir NANDA SÁ", "Discover NANDA SÁ", "Descobrir a NANDA SÁ")}
            <ArrowDown size={15} strokeWidth={1.5} />
          </a>
          <Link
            to="/contact"
            data-testid="hero-cta-contact"
            className="inline-flex items-center gap-2.5 text-sm font-medium text-nanda-ink link-underline"
          >
            {tt("Nous contacter", "Contact us", "Contactar-nos")}
            <ArrowUpRight size={15} strokeWidth={1.5} />
          </Link>
        </FadeUp>
      </div>
      <FadeUp delay={0.35} className="mx-auto max-w-7xl px-6 sm:px-12 pb-20 md:pb-28">
        <div className="overflow-hidden rounded-3xl">
          <motion.img
            initial={{ scale: 1.04 }}
            animate={{ scale: 1 }}
            transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
            src="/photos/home-hero-bissau.jpg"
            alt={tt(
              "Bissau, capitale de la Guinée-Bissau, ouverte sur l'océan Atlantique",
              "Bissau, capital of Guinea-Bissau, opening onto the Atlantic Ocean",
              "Bissau, capital da Guiné-Bissau, aberta sobre o oceano Atlântico"
            )}
            className="w-full aspect-[16/10] md:aspect-[16/7] object-cover"
            loading="eager"
          />
        </div>
      </FadeUp>
    </section>
  );
};

const Universe = ({ u, index }) => {
  const even = index % 2 === 0;
  return (
    <section data-testid={`universe-card-${u.id}`} className="border-t border-nanda-line">
      <div className="mx-auto max-w-7xl px-6 sm:px-12 py-20 md:py-32 grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-20 items-center">
        <FadeUp className={even ? "md:order-1" : "md:order-2"}>
          <Link to={u.to} className="group block overflow-hidden rounded-3xl" tabIndex={-1} aria-hidden="true">
            <img
              src={u.img}
              alt={u.alt}
              loading="lazy"
              className="w-full aspect-[4/3] object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.03]"
            />
          </Link>
        </FadeUp>
        <FadeUp delay={0.12} className={even ? "md:order-2" : "md:order-1"}>
          <div className="label-overline text-nanda-terracotta mb-6">{u.badge}</div>
          <h2 className="font-sans font-light tracking-tight text-nanda-ink text-3xl sm:text-4xl lg:text-5xl leading-[1.1] text-balance">
            {u.title}
          </h2>
          <p className="mt-7 max-w-lg text-nanda-inkSoft text-base md:text-lg font-light leading-relaxed">
            {u.desc}
          </p>
          <Link
            to={u.to}
            data-testid={`universe-cta-${u.id}`}
            className="btn-arrow mt-10 inline-flex items-center gap-2.5 text-sm font-medium text-nanda-ink link-underline"
          >
            {u.cta}
            <ArrowUpRight size={15} strokeWidth={1.5} />
          </Link>
        </FadeUp>
      </div>
    </section>
  );
};

const Universes = () => {
  const tt = useT();
  const UNIVERSES = [
    {
      id: "pro",
      badge: "NANDA SÁ PRO",
      title: tt("L'énergie, bien pensée.", "Energy, well designed.", "Energia, bem pensada."),
      desc: tt(
        "Le solaire est une solution d'avenir. Nous assurons le diagnostic, le dimensionnement, la fourniture des équipements et l'installation, du premier relevé à la mise en service.",
        "Solar is a solution for the future. We handle assessment, sizing, equipment supply and installation, from the first survey to commissioning.",
        "O solar é uma solução de futuro. Asseguramos o diagnóstico, o dimensionamento, o fornecimento dos equipamentos e a instalação, do primeiro levantamento à entrada em serviço."
      ),
      to: "/marques/energia-pro",
      cta: tt("Explorer NANDA SÁ PRO", "Explore NANDA SÁ PRO", "Explorar a NANDA SÁ PRO"),
      img: "/photos/sector-energie-hero.jpg",
      alt: tt("Installation solaire photovoltaïque en Guinée-Bissau", "Photovoltaic solar installation in Guinea-Bissau", "Instalação solar fotovoltaica na Guiné-Bissau"),
    },
    {
      id: "cosmetiques",
      badge: tt("Cosmétiques", "Cosmetics", "Cosméticos"),
      title: tt("Le soin par les matières naturelles.", "Care through natural materials.", "O cuidado pelas matérias naturais."),
      desc: tt(
        "Chantillys, beurres et huiles formulés à partir du karité, du baobab et de l'hibiscus de Guinée-Bissau. Des formules courtes, fabriquées localement.",
        "Whipped butters and oils formulated from Guinea-Bissau shea, baobab and hibiscus. Short formulas, made locally.",
        "Chantillys, manteigas e óleos formulados a partir do karité, do baobá e do hibisco da Guiné-Bissau. Fórmulas curtas, fabricadas localmente."
      ),
      to: "/cosmetiques",
      cta: tt("Découvrir la collection", "Discover the collection", "Descobrir a colecção"),
      img: "/photos/nanda-cosmetiques/chantilly-baobab-karite.jpg",
      alt: tt("Chantilly Baobab Karité NANDA SÁ, pot de 100 ml", "NANDA SÁ Baobab Shea Whipped Butter, 100 ml jar", "Chantilly Baobá Karité NANDA SÁ, frasco de 100 ml"),
    },
    {
      id: "terroirs",
      badge: tt("Produits & Terroirs", "Products & Terroirs", "Produtos & Terroirs"),
      title: tt("Simplement précieux.", "Simply precious.", "Simplesmente precioso."),
      desc: tt(
        "Fleur de sel et miel, récoltés avec soin au Sénégal. Deux produits, une même exigence de qualité et de traçabilité.",
        "Fleur de sel and honey, carefully harvested in Senegal. Two products, one same standard of quality and traceability.",
        "Flor de sal e mel, colhidos com cuidado no Senegal. Dois produtos, a mesma exigência de qualidade e rastreabilidade."
      ),
      to: "/terroirs",
      cta: tt("Découvrir les Terroirs", "Discover the Terroirs", "Descobrir os Terroirs"),
      img: "/photos/nanda-sa-food/miel.jpg",
      alt: tt("Miel du Sénégal", "Senegal honey", "Mel do Senegal"),
    },
    {
      id: "academie",
      badge: tt("Académie", "Academy", "Academia"),
      title: tt("Comprendre. Apprendre. Avancer.", "Understand. Learn. Move forward.", "Compreender. Aprender. Avançar."),
      desc: tt(
        "Des guides, des ressources, des programmes et des ateliers pour apprendre, se former et avancer.",
        "Guides, resources, programmes and workshops to learn, train and move forward.",
        "Guias, recursos, programas e ateliers para aprender, formar-se e avançar."
      ),
      to: "/marques/academie",
      cta: tt("Découvrir l'Académie", "Discover the Academy", "Descobrir a Academia"),
      img: "/photos/academie-hero.jpg",
      alt: tt("NANDA SÁ Académie, transmission des savoirs au Sénégal et en Guinée-Bissau", "NANDA SÁ Academy, knowledge transmission in Senegal and Guinea-Bissau", "NANDA SÁ Academia, transmissão de saberes no Senegal e na Guiné-Bissau"),
    },
  ];
  return (
    <div id="univers" className="scroll-anchor" data-testid="home-universes">
      {UNIVERSES.map((u, i) => (
        <Universe key={u.id} u={u} index={i} />
      ))}
    </div>
  );
};

const Manifesto = () => {
  const tt = useT();
  return (
    <section data-testid="home-manifesto-section" className="border-t border-nanda-line bg-nanda-bgAlt">
      <div className="mx-auto max-w-5xl px-6 sm:px-12 py-24 md:py-36">
        <FadeUp>
          <div className="label-overline text-nanda-muted mb-8">{tt("Le Manifeste", "The Manifesto", "O Manifesto")}</div>
        </FadeUp>
        <FadeUp delay={0.1}>
          <h2 className="font-sans font-light tracking-tight text-nanda-ink text-3xl sm:text-4xl lg:text-6xl leading-[1.12] text-balance">
            {tt(
              "Une entreprise qui fabrique, installe et transmet.",
              "A company that makes, installs and teaches.",
              "Uma empresa que fabrica, instala e transmite."
            )}
          </h2>
        </FadeUp>
        <FadeUp delay={0.2}>
          <p className="mt-10 max-w-2xl text-nanda-inkSoft text-base md:text-lg font-light leading-relaxed">
            {tt(
              "Nous installons des systèmes solaires, nous fabriquons des cosmétiques naturels, nous vendons des produits du terroir et nous formons ceux qui veulent apprendre. Entre la Guinée-Bissau et le Sénégal, et pour durer.",
              "We install solar systems, we make natural cosmetics, we sell terroir products and we train those who want to learn. Between Guinea-Bissau and Senegal, and built to last.",
              "Instalamos sistemas solares, fabricamos cosméticos naturais, vendemos produtos do terroir e formamos quem quer aprender. Entre a Guiné-Bissau e o Senegal, e para durar."
            )}
          </p>
        </FadeUp>
        <FadeUp delay={0.3} className="mt-10">
          <Link
            to="/maison"
            data-testid="manifesto-link"
            className="btn-arrow inline-flex items-center gap-2.5 text-sm font-medium text-nanda-ink link-underline"
          >
            {tt("Découvrir la Maison", "Discover the House", "Descobrir a Casa")}
            <ArrowUpRight size={15} strokeWidth={1.5} />
          </Link>
        </FadeUp>
      </div>
    </section>
  );
};

const News = () => {
  const tt = useT();
  const { locale } = useLanguage();
  const ACTUS = useActualites().slice(0, 3);
  const dateLocale = locale === "en" ? "en-GB" : locale === "pt" ? "pt-PT" : "fr-FR";
  const formatDate = (iso) =>
    iso ? new Date(iso).toLocaleDateString(dateLocale, { day: "numeric", month: "long", year: "numeric" }) : null;
  if (!ACTUS.length) return null;
  return (
    <section data-testid="home-news" className="border-t border-nanda-line bg-white">
      <div className="mx-auto max-w-7xl px-6 sm:px-12 py-24 md:py-32">
        <FadeUp className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
          <div>
            <div className="label-overline text-nanda-muted mb-6">{tt("Actualités", "News", "Actualidades")}</div>
            <h2 className="font-sans font-light tracking-tight text-nanda-ink text-3xl sm:text-4xl lg:text-5xl leading-[1.1] text-balance">
              {tt("Ce qui se passe, en ce moment.", "What is happening, right now.", "O que se passa, neste momento.")}
            </h2>
          </div>
          <div className="flex flex-wrap items-center gap-8">
            <Link
              to="/actualites"
              data-testid="home-news-all"
              className="inline-flex items-center gap-2.5 text-sm font-medium text-nanda-ink link-underline"
            >
              {tt("Toutes les actualités", "All news", "Todas as actualidades")}
              <ArrowUpRight size={15} strokeWidth={1.5} />
            </Link>
            <Link
              to="/carnet"
              data-testid="home-news-carnet"
              className="inline-flex items-center gap-2.5 text-sm font-medium text-nanda-ink link-underline"
            >
              {tt("Lire le Carnet NANDA", "Read the NANDA Journal", "Ler o Caderno NANDA")}
              <ArrowUpRight size={15} strokeWidth={1.5} />
            </Link>
          </div>
        </FadeUp>
        <Stagger className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {ACTUS.map((a) => (
            <StaggerItem key={a.slug}>
              <Link to={`/actualites/${a.slug}`} data-testid={`home-actu-${a.slug}`} className="group block">
                <div className="overflow-hidden rounded-3xl mb-6">
                  <img
                    src={a.cover}
                    alt={a.coverAlt || a.title}
                    loading="lazy"
                    className="w-full aspect-[4/3] object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.03]"
                  />
                </div>
                <div className="label-overline text-nanda-terracotta">
                  {a.universLabel}{a.date ? ` · ${formatDate(a.date)}` : ""}
                </div>
                <h3 className="font-sans font-medium tracking-tight text-xl md:text-2xl text-nanda-ink mt-4 leading-tight group-hover:text-nanda-terracotta transition-colors duration-300">
                  {a.title}
                </h3>
                <p className="mt-3 text-nanda-inkSoft text-sm font-light leading-relaxed">{a.excerpt}</p>
              </Link>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
};

const ContactCTA = () => {
  const tt = useT();
  return (
    <section data-testid="home-contact-cta" className="border-t border-nanda-line bg-white">
      <div className="mx-auto max-w-7xl px-6 sm:px-12 py-24 md:py-36 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
        <FadeUp className="md:col-span-8">
          <h2 className="font-sans font-light tracking-tight text-nanda-ink text-3xl sm:text-4xl lg:text-6xl leading-[1.1] text-balance">
            {tt(
              "Un projet, une question, une collaboration ?",
              "A project, a question, a collaboration?",
              "Um projecto, uma questão, uma colaboração?"
            )}
          </h2>
        </FadeUp>
        <FadeUp delay={0.15} className="md:col-span-4 flex md:justify-end">
          <Link
            to="/contact"
            data-testid="home-cta-contact"
            className="inline-flex items-center gap-2.5 bg-nanda-terracotta text-white rounded-full px-7 py-3.5 text-sm font-medium hover:bg-[#8F5B3F] transition-colors duration-300"
          >
            {tt("Prendre contact", "Get in touch", "Entrar em contacto")}
            <ArrowUpRight size={15} strokeWidth={1.5} />
          </Link>
        </FadeUp>
      </div>
    </section>
  );
};

export default function Home() {
  return (
    <div data-testid="page-home">
      <Seo />
      <Hero />
      <Universes />
      <Manifesto />
      <News />
      <ContactCTA />
    </div>
  );
}
